import { useMemo } from "react"
import { Box, Grid, HStack, SimpleGrid, Table, Text, VStack } from "@chakra-ui/react"
import { ColumnDef } from "@tanstack/react-table"
import { format, formatDistanceToNowStrict } from "date-fns"
import PaginatedTable from "@hydrologiq/shared/components/PaginatedTable"
import Link from "@hydrologiq/shared/components/Link"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { ScopeLinkChip, SCOPE_CHIP_COLOURS } from "@hydrologiq/shared/components/ScopeLinkChip"
import { Chip as SharedChip, StatusTag } from "@hydrologiq/shared/components/Chip"
import { EXPANDED_ROW_BACKGROUND, expandedRowSpacerCellStyle } from "@hydrologiq/shared/components/expandedRowStyles"
import { formatChipLabel } from "@hydrologiq/shared/components/alerts/AlertRuleCard"
import { connectionHealth, ConnectionHealthState, parseUtc } from "../utils/connectionHealth"
import { DataConnectionListItem, useDataConnectionsList } from "../hooks/useDataConnectionsList"

// Palettes from docs/architecture/ui-visual-vocabulary.md (§1D state, §1A severity)
const GREEN: [string, string] = ["#C6F6D5", "#22543D"]
const GREY: [string, string] = ["#EDF2F7", "#718096"]
const AMBER: [string, string] = ["#FEEBC8", "#7B341E"]
const HEALTH: Record<ConnectionHealthState, { label: string; colours: [string, string] }> = {
  ok: { label: "OK", colours: GREEN },
  inactive: { label: "Inactive", colours: GREY },
  unmapped: { label: "Unmapped", colours: AMBER },
  "backing-off": { label: "Backing Off", colours: AMBER },
  stale: { label: "Stale", colours: AMBER },
}

/** The site's chip, in these colours. */
function Chip({ colours: [bg, color], children }: { colours: [string, string]; children: React.ReactNode }) {
  return <SharedChip colours={{ bg, color }}>{children}</SharedChip>
}

const relative = (value?: string | null) =>
  value ? formatDistanceToNowStrict(parseUtc(value), { addSuffix: true }) : "Never"
const absolute = (value?: string | null) => (value ? format(parseUtc(value), "dd MMM yyyy HH:mm:ss") : "–")
const holderHref = (kind: string, id: string) =>
  `/${kind === "system" ? "systems" : "assets"}/${id}/settings?tab=connections`

/** Single-line text that truncates, with the full value (or extra context) in a tooltip. */
function OneLine({ text, tooltip, maxW = "260px", mono }: { text: string; tooltip?: string; maxW?: string; mono?: boolean }) {
  return (
    <Tooltip content={tooltip ?? text}>
      <Text fontSize="sm" truncate minW={0} maxW={maxW} fontFamily={mono ? "mono" : undefined}>
        {text}
      </Text>
    </Tooltip>
  )
}

function Fact({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <Box minW={0}>
      <Text fontSize="xs" color="fg.muted">
        {label}
      </Text>
      <Text fontSize="sm" fontFamily={mono ? "mono" : undefined} truncate title={typeof children === "string" ? children : undefined}>
        {children}
      </Text>
    </Box>
  )
}

function DetailCard({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <Box bg="bg.panel" borderWidth="1px" borderRadius="md" p={4} minW={0}>
      <Text fontWeight="semibold" fontSize="sm">
        {title}
      </Text>
      {note && (
        <Text fontSize="xs" color="fg.muted">
          {note}
        </Text>
      )}
      <Box mt={3}>{children}</Box>
    </Box>
  )
}

// One template for the header and every row, so names, chips and sources line up in columns
const ASSET_GRID = "minmax(0, 2fr) 110px minmax(0, 1.5fr) minmax(0, 1fr)"

function MappedAssetsGrid({ assets }: { assets: DataConnectionListItem["mappedAssets"] }) {
  if (assets.length === 0) {
    return (
      <Text fontSize="sm" color="fg.muted">
        None. The ingest worker skips a connection with no mapped assets.
      </Text>
    )
  }
  return (
    <VStack align="stretch" gap={0}>
      <Grid templateColumns={ASSET_GRID} gap={3} pb={1} borderBottomWidth="1px">
        {["Asset", "Type", "Device Source", "Ref"].map((h) => (
          <Text key={h} fontSize="xs" color="fg.muted">
            {h}
          </Text>
        ))}
      </Grid>
      {assets.map((a) => (
        <Grid key={`${a.assetId}-${a.sourceRef}`} templateColumns={ASSET_GRID} gap={3} py={1.5} alignItems="center" borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }}>
          <Box minW={0}>
            <Link href={`/assets/${a.assetId}`} external fontSize="sm" truncate>
              {a.assetName ?? a.assetId}
            </Link>
          </Box>
          <Box>{a.assetClass && <Chip colours={GREY}>{formatChipLabel(a.assetClass)}</Chip>}</Box>
          <OneLine text={a.sourceLabel ?? "–"} maxW="100%" />
          <OneLine text={a.sourceRef ?? "–"} maxW="100%" mono />
        </Grid>
      ))}
    </VStack>
  )
}

function ConnectionDetails({ connection: c }: { connection: DataConnectionListItem }) {
  const { runState, pollSchedule } = c.dataConnector
  const isSystemHeld = c.holderKind === "system"
  return (
    <Box py={2}>
      <Grid templateColumns={{ base: "1fr", xl: "minmax(0, 1.2fr) minmax(0, 1fr)" }} gap={4}>
        <DetailCard
          title={isSystemHeld ? `Assets on this connection (${c.mappedAssets.length})` : "This asset's connection"}
          note={isSystemHeld ? "Mapped through the System's connection." : undefined}
        >
          {!isSystemHeld && (c.deployedSystemId || c.parentAssetId) && (
            <HStack gap={2} mb={3} flexWrap="wrap">
              {c.deployedSystemId && (
                <HStack gap={1.5}>
                  <Text fontSize="xs" color="fg.muted">Deployed in</Text>
                  <ScopeLinkChip label="System" href={holderHref("system", c.deployedSystemId)} entityName={c.deployedSystemName ?? c.deployedSystemId} colours={SCOPE_CHIP_COLOURS.neutral} />
                  <Text fontSize="sm">{c.deployedSystemName}</Text>
                </HStack>
              )}
              {c.parentAssetId && (
                <HStack gap={1.5}>
                  <Text fontSize="xs" color="fg.muted">Part of</Text>
                  <ScopeLinkChip label="Asset Group" href={holderHref("asset", c.parentAssetId)} entityName={c.parentAssetName ?? c.parentAssetId} />
                  <Text fontSize="sm">{c.parentAssetName}</Text>
                </HStack>
              )}
            </HStack>
          )}
          <MappedAssetsGrid assets={c.mappedAssets} />
        </DetailCard>

        <DetailCard title="Diagnostics">
          <SimpleGrid columns={3} gap={3}>
            <Fact label="External Id" mono>{c.externalId ?? "–"}</Fact>
            <Fact label="Monitoring Instance" mono>{c.id}</Fact>
            <Fact label="Monitor">{c.monitorName ?? c.monitorId}</Fact>
            <Fact label="Installation" mono>{c.installationId ?? "–"}</Fact>
            <Fact label="Holder" mono>{c.holderId ?? "–"}</Fact>
            <Fact label="Data Connector Id" mono>{c.dataConnector.id}</Fact>
            <Fact label="Last queried">{absolute(c.lastQueried)}</Fact>
            <Fact label="Created">{absolute(c.createdAt)}</Fact>
            <Fact label="Updated">{absolute(c.updatedAt)}</Fact>
          </SimpleGrid>
          <Box borderTopWidth="1px" my={3} />
          <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
            Data Connector
          </Text>
          <Text fontSize="xs" color="fg.muted" mb={2}>
            Run state below is shared by every connection on this Data Connector.
          </Text>
          <SimpleGrid columns={3} gap={3}>
            <Fact label="Last success">{absolute(runState?.lastSuccessAt)}</Fact>
            <Fact label="Last fetched">{absolute(runState?.lastFetchedAt)}</Fact>
            <Fact label="Next poll">{absolute(pollSchedule?.nextPollAt)}</Fact>
            <Fact label="Backoff until">{absolute(runState?.backoffUntil ?? pollSchedule?.backoffUntil)}</Fact>
            <Fact label="Retries">{String(runState?.retryCount ?? "–")}</Fact>
            <Fact label="Last error">{runState?.lastError ?? pollSchedule?.lastError ?? "–"}</Fact>
          </SimpleGrid>
        </DetailCard>
      </Grid>
    </Box>
  )
}

export function DataConnectionsTab() {
  const { connections, loading, error, refetch } = useDataConnectionsList()

  const columns = useMemo<ColumnDef<DataConnectionListItem, any>[]>(
    () => [
      {
        id: "externalId",
        header: "External Id",
        size: 120,
        minSize: 120,
        accessorFn: (c) => c.externalId ?? "",
        cell: ({ getValue }) => <OneLine text={getValue() || "–"} maxW="110px" mono />,
        meta: { filterVariant: "text" },
      },
      {
        id: "dataConnector",
        header: "Data Connector",
        minSize: 220,
        accessorFn: (c) => c.dataConnector.name ?? c.dataConnector.id,
        cell: ({ row: { original: c } }) => (
          <HStack gap={2} minW={0}>
            <Chip colours={GREY}>{c.dataConnector.connectorType}</Chip>
            <OneLine text={c.dataConnector.name ?? c.dataConnector.id} />
          </HStack>
        ),
        meta: { filterVariant: "select" },
      },
      {
        id: "heldBy",
        header: "Held By",
        minSize: 240,
        accessorFn: (c) => formatChipLabel(c.holderKind),
        cell: ({ row: { original: c } }) => {
          const name = c.holderName ?? c.holderId ?? "No holder"
          const context = c.deployedSystemName ? `In ${c.deployedSystemName}` : c.parentAssetName ? `Part of ${c.parentAssetName}` : ""
          return (
            <HStack gap={2} minW={0}>
              {c.holderId ? (
                <ScopeLinkChip
                  label={formatChipLabel(c.holderKind)}
                  href={holderHref(c.holderKind, c.holderId)}
                  entityName={name}
                  colours={c.holderKind === "asset" ? SCOPE_CHIP_COLOURS.asset : SCOPE_CHIP_COLOURS.neutral}
                />
              ) : (
                <Chip colours={GREY}>None</Chip>
              )}
              {c.holderKind === "asset" && c.parentAssetId && (
                <Chip colours={[SCOPE_CHIP_COLOURS.asset.bg, SCOPE_CHIP_COLOURS.asset.color]}>Module</Chip>
              )}
              <OneLine
                text={name}
                tooltip={context ? `${name}. ${context}` : name}
                maxW={c.holderKind === "asset" && c.parentAssetId ? "200px" : "260px"}
              />
            </HStack>
          )
        },
        meta: { filterVariant: "select" },
      },
      {
        id: "status",
        header: "Status",
        size: 100,
        minSize: 100,
        accessorFn: (c) => formatChipLabel(c.status),
        cell: ({ getValue }) => <StatusTag active={getValue() === "Active"} />,
        meta: { filterVariant: "select" },
      },
      {
        id: "health",
        header: "Health",
        size: 110,
        minSize: 110,
        accessorFn: (c) => HEALTH[connectionHealth(c).state].label,
        cell: ({ row }) => {
          const health = connectionHealth(row.original)
          return (
            <Tooltip content={health.reason} showArrow>
              <span>
                <Chip colours={HEALTH[health.state].colours}>{HEALTH[health.state].label}</Chip>
              </span>
            </Tooltip>
          )
        },
        meta: { filterVariant: "select" },
      },
      // Header holds an unbreakable label plus sort and filter icons, so size the column for that.
      // A text filter on a count isn't useful; sorting still works.
      { id: "mappings", header: "Mappings", size: 105, minSize: 105, accessorKey: "activeMappingCount", enableColumnFilter: false },
      {
        id: "lastQueried",
        header: "Last Queried",
        size: 125,
        minSize: 125,
        enableColumnFilter: false,
        accessorFn: (c) => c.lastQueried ?? "",
        cell: ({ row }) => (
          <Tooltip content={absolute(row.original.lastQueried)}>
            <Text fontSize="sm" whiteSpace="nowrap">
              {relative(row.original.lastQueried)}
            </Text>
          </Tooltip>
        ),
      },
    ],
    [],
  )

  return (
    <Box py={4}>
      <PaginatedTable
        data={connections}
        columns={columns}
        loading={loading && connections.length === 0}
        error={error?.message}
        onRetry={() => refetch()}
        entityName="connections"
        emptyState={{ heading: "No connections", text: "No data connections in this organisation yet." }}
        expandableRows={{
          getRowId: (c) => c.id,
          // Same row background and tree strip as the asset table's expanded rows
          renderExpandedRows: (c, rowColumns) => (
            <Table.Row style={{ background: EXPANDED_ROW_BACKGROUND }}>
              <Table.Cell style={expandedRowSpacerCellStyle} />
              <Table.Cell
                colSpan={rowColumns.length}
                style={{ padding: "0.55rem 0.625rem", paddingLeft: "0.125rem", borderBottom: "1px solid #E2E8F0" }}
              >
                <ConnectionDetails connection={c} />
              </Table.Cell>
            </Table.Row>
          ),
        }}
      />
    </Box>
  )
}

export default DataConnectionsTab
